import { z } from "zod";

export const CACHE_VERSION = 1;
export const CACHE_DB_IDENTIFIER = "mem/cache-v2";
export const CACHE_TABLE_IDENTIFIER = "cache";

export const GLOBAL_CACHE_DB_IDENTIFIER = "mem/global-cache-v1";

export const ACCOUNT_CACHE_DB_IDENTIFIER = "mem/account-cache-v1";

export enum CacheKey {
  ActiveClientInfo = "ACTIVE_CLIENT_INFO",
  MyAccount = "MY_ACCOUNT",
  MySpaces = "MY_SPACES",
  MySpaceAccounts = "MY_SPACE_ACCOUNTS",
  FeatureGateConfigs = "FEATURE_GATE_CONFIGS",
  ImportSessionsInProgress = "IMPORT_SESSIONS_IN_PROGRESS",
  AssetAccessToken = "ASSET_ACCESS_TOKEN",
  ActiveAudioRecording = "ACTIVE_AUDIO_RECORDING",
  MetricsSegmentAnonymousId = "METRICS_SEGMENT_ANONYMOUS_ID",
  MetricsGoogleAnalyticsClientId = "METRICS_GOOGLE_ANALYTICS_CLIENT_ID",
  MetricsGoogleAnalyticsSessionId = "METRICS_GOOGLE_ANALYTICS_SESSION_ID",
  MetricsMetadataString = "METRICS_METADATA_STRING",
  MetricsElectronAppInfo = "METRICS_ELECTRON_APP_INFO",
  PerDeviceOnboardingInfo = "PER_DEVICE_ONBOARDING_INFO_V3",
}

export const ActiveClientInfo = z.object({
  clientGroupId: z.string().uuid(),
});

export const MyAccountCacheSchema = z.object({
  id: z.string().uuid(),
  profileEmailAddress: z.string(),
  profileDisplayName: z.string(),
  profilePhotoUrl: z.string().nullable(),
  createdAt: z.string().nullable(),
  settings: z.record(z.string(), z.unknown()).optional(),
});

export const MySpacesCacheSchema = z.array(
  z.object({
    id: z.string().uuid(),
    title: z.string(),
  })
);

export const MySpaceAccountsCacheSchema = z.array(
  z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    accountId: z.string().uuid(),
    profileEmailAddress: z.string(),
    profileDisplayName: z.string(),
    profilePhotoUrl: z.string().nullable(),
  })
);

export const FeatureGateConfigDataSchema = z.object({
  period: z.string(),
  limits: z.object({
    free: z.number().nullable(),
    paid: z.number().nullable(),
  }),
});

export const FeatureGateConfigsCacheSchema = z.record(z.string(), FeatureGateConfigDataSchema);

export const ImportSessionsInProgressCacheSchema = z.array(
  z.object({
    sessionId: z.string().uuid(),
    createdAtMs: z.number(),
  })
);

export const AssetAccessTokenCacheSchema = z.object({
  token: z.string(),
  expiresAt: z.string().datetime().nullable(),
});

export const MetricsSegmentAnonymousIdCacheSchema = z.object({
  segmentAnonymousId: z.string(),
});

export const MetricsGoogleAnalyticsClientIdCacheSchema = z.object({
  googleAnalyticsClientId: z.string(),
});

export const MetricsGoogleAnalyticsSessionIdCacheSchema = z.object({
  googleAnalyticsSessionId: z.string(),
});

export const MetricsMetadataStringCacheSchema = z.object({
  metadataString: z.string(),
});

export const MetricsElectronAppInfoCacheSchema = z.object({
  electronSystemArchitecture: z.string(),
  electronAppVersion: z.string(),
  electronRuntimeVersion: z.string(),
});

export const ActiveAudioRecordingCacheSchema = z.object({
  audioRecordingId: z.uuid(),
});

const PerDeviceOnboardingInfoCacheSchema = z.object({
  hasDismissedMeetingOnboardingSystemPermissions: z.boolean(),
  hasDismissedVoiceOnboardingSystemPermissions: z.boolean(),
});

export const CACHE_SCHEMA = {
  [CacheKey.ActiveClientInfo]: ActiveClientInfo,
  [CacheKey.MyAccount]: MyAccountCacheSchema,
  [CacheKey.MySpaces]: MySpacesCacheSchema,
  [CacheKey.MySpaceAccounts]: MySpaceAccountsCacheSchema,
  [CacheKey.FeatureGateConfigs]: FeatureGateConfigsCacheSchema,
  [CacheKey.ImportSessionsInProgress]: ImportSessionsInProgressCacheSchema,
  [CacheKey.AssetAccessToken]: AssetAccessTokenCacheSchema,
  [CacheKey.ActiveAudioRecording]: ActiveAudioRecordingCacheSchema,
  [CacheKey.MetricsSegmentAnonymousId]: MetricsSegmentAnonymousIdCacheSchema,
  [CacheKey.MetricsGoogleAnalyticsClientId]: MetricsGoogleAnalyticsClientIdCacheSchema,
  [CacheKey.MetricsGoogleAnalyticsSessionId]: MetricsGoogleAnalyticsSessionIdCacheSchema,
  [CacheKey.MetricsMetadataString]: MetricsMetadataStringCacheSchema,
  [CacheKey.MetricsElectronAppInfo]: MetricsElectronAppInfoCacheSchema,
  [CacheKey.PerDeviceOnboardingInfo]: PerDeviceOnboardingInfoCacheSchema,
};

/**
 * Mapping of CacheKey to the type of the data that is stored
 */
export type CacheSchemaType = {
  [K in CacheKey]: z.infer<(typeof CACHE_SCHEMA)[K]>;
};
